import { useSyncExternalStore } from "react";
import catalog from "./catalog.json";
import textLabels from "./textLabels.json";

const PREFIX = "dakikanoss:web:translations:v1:";
const LANGUAGES = ["en", "ar", "fr", "gr"];
const listeners = new Set();
const memory = {};
let revision = 0;
const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(PREFIX + key)) ?? fallback;
  } catch {
    return fallback;
  }
};
const write = (key, value) => {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* Translations remain available in memory when storage is unavailable. */
  }
};
let language = read("language", "en");
if (!LANGUAGES.includes(language)) language = "en";
let scope = "web";
const cachedSources = (lang) => {
  if (!memory[lang]) {
    const saved = read(lang, {});
    memory[lang] =
      saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  }
  return memory[lang];
};
cachedSources(language);
const notify = () => {
  revision += 1;
  listeners.forEach((listener) => listener());
};
export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getSnapshot = () => revision;
export const getLanguage = () => language;
export function setLanguage(next) {
  if (!LANGUAGES.includes(next) || next === language) return;
  cachedSources(next);
  language = next;
  write("language", next);
  notify();
}
export function setScope(next) {
  if (scope !== next) {
    scope = next;
    notify();
  }
}
export function saveSource(lang, source, labels) {
  cachedSources(lang)[source] = labels;
  write(lang, memory[lang]);
  if (language === lang) notify();
}
const apiValue = (value) =>
  typeof value === "string" && value.trim() ? value : undefined;
export function translateLabel(id, fallback = "", values = {}) {
  const sources = cachedSources(language);
  const apiText =
    apiValue(sources[scope]?.[id]) ?? apiValue(sources.common?.[id]);
  // Vendor/web UI uses API text only. Admin migration is a separate phase.
  if (scope === "web") {
    return String(apiText ?? "").replace(/\{\{?(\w+)\}?\}/g, (match, key) =>
      values[key] == null ? match : String(values[key]),
    );
  }
  const entry = catalog[id];
  const allowed =
    entry &&
    (entry.destination === 0 ||
      entry.destination === (scope === "admin" ? 4 : 1));
  const bundled = allowed ? entry?.[language] : undefined;
  const text = apiText || bundled || fallback || entry?.en || id;
  return String(text).replace(/\{\{?(\w+)\}?\}/g, (match, key) =>
    values[key] == null ? match : String(values[key]),
  );
}
const normalize = (value) =>
  value
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[.!…]+$/, "");
const aliases = new Map(
  Object.entries(textLabels).map(([text, id]) => [normalize(text), id]),
);
const templates = Object.entries(textLabels)
  .filter(([text]) => /\{value\d+\}/.test(text))
  .map(([text, id]) => {
    const keys = [];
    const escaped = text
      .split(/(\{value\d+\})/)
      .map((part) => {
        if (/^\{value\d+\}$/.test(part)) {
          keys.push(part.slice(1, -1));
          return "(.+?)";
        }
        return part
          .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
          .replace(/\s+/g, "\\s+");
      })
      .join("");
    return { id, keys, pattern: new RegExp(`^${escaped}$`, "i") };
  });
// Use only at app-owned UI boundaries. Product names, user data, API values and
// submitted option values stay in their original form.
export function translateText(value) {
  if (typeof value !== "string" || !value.trim()) return value;
  const id = aliases.get(normalize(value));
  if (id) return translateLabel(id, value);
  for (const template of templates) {
    const match = template.pattern.exec(value.trim());
    if (match)
      return translateLabel(
        template.id,
        value,
        Object.fromEntries(
          template.keys.map((key, index) => [key, match[index + 1]]),
        ),
      );
  }
  return value;
}
export function translateDocument(value) {
  return typeof value === "string"
    ? value
        .split(/(\n\s*\n)/)
        .map(translateText)
        .join("")
    : value;
}
export function cleanRequiredLabel(value) {
  return typeof value === "string"
    ? value.replace(/(?:\s*[*＊])+\s*$/u, "")
    : value;
}
export function translateFieldLabel(id, fallback = "") {
  return cleanRequiredLabel(translateLabel(id, fallback));
}
export function useTranslationSnapshot() {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
export function useTranslation() {
  useTranslationSnapshot();
  return {
    language,
    isRTL: language === "ar",
    setLanguage,
    t: translateLabel,
    translateText,
  };
}
